import { useEffect, useState } from 'react';
import Badge from '../../../components/badge/Badge';
import Card from '../../../components/card/Card';
import Table from '../../../components/table/table';
import { useClientTheme } from '../../../context/ThemeContext';
import P from '../../../components/texts/p/P';
import { riskEntities } from '../../../mocks/RiskEntities';
import { MONTH_NAMES, RISK_CODES, RISK_STATUS } from '../../../utils/Constants';
import { balanceInfo, indicator, riskData } from '../../../types/interfaces.tsx';
import Span from '../../../components/texts/span/Span';
import Tooltip from '../../../components/tooltip/Tooltip';
import Spinner from '../../../components/Spinner/spinner';
import Image from '../../../components/image/Image';
import Icon from '../../../components/icon/Icon';
import Button from '../../../components/button/Button';
import { Toast, useToast } from '../../../components/toast/toast';
import Collapsable from '../../../components/collapsable/Collapsable';
import FilePreview from '../../common/FilePreview';
import Visualizer from '../Visualizer';
import { formatCurrency } from '../../../utils/Masks';
import { toNumber } from '../../../utils/Utils';
import H from '../../../components/texts/h/H';
import Divider from '../../../components/divider/Divider';
import { standardizeBalanceData } from '../../../utils/BalanceDataUtils';
import { StandardizedBalanceData } from '../../../types/interfaces.tsx';
import {
  RiskDataEntry,
  RiskDataMap,
  PostBalanceRow,
  FallOfDebtRow,
  SgrRow,
  SgrInstrumentRow,
  CheckLightResumeProps,
  BankDetail,
  SgrItem,
  SgrInstrumentDetail,
  SgrByInstrumentEntry,
} from '../../../types/risk.types';

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowSize(window.innerWidth);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

// Función para verificar si los datos ya están en formato estandarizado
const isStandardizedBalanceData = (
  data: balanceInfo | StandardizedBalanceData | undefined | null
): data is StandardizedBalanceData => {
  return !!(
    data && typeof data === 'object' && 'values' in data && 'totals' in data
  );
};

const CheckLightResume: React.FC<CheckLightResumeProps> = ({
  data,
  showBalanceInfo,
  isLoading,
  handleRiskData,
  handleRiskDataAndDebts,
  riskCodes,
  debts,
  documents,
  cuit,
  hideInfo = true,
  balanceData,
  postBalanceData,
  isPdfMode = false,
  onDownloadPdf,
  isPdfLoading = false,
}) => {
  const { clientTheme } = useClientTheme();
  const config = JSON.parse(localStorage.getItem('config') ?? '{}');
  const isCasfogEnabled = config?.isCasfogEnabled ?? true;

  const windowWidth = useWindowSize();
  const [displayFlex, setDisplayFlex] = useState(
    windowWidth > 1850 ? 'flex' : 'block'
  );
  const [riskDataMap, setRiskDataMap] = useState<RiskDataMap | undefined>(
    undefined
  );
  const [lastExecution, setLastExecution] = useState('');
  const [expandedSgrRows, setExpandedSgrRows] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    setDisplayFlex(windowWidth > 1850 ? 'flex' : 'block');
  }, [windowWidth]);

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const transformedData = transformData(data);
      setRiskDataMap(transformedData);
      setLastExecution(
        getLastExecutionFromData(
          Object.values(transformedData).filter(
            (item) => item.risk_code !== RISK_CODES.bpb
          )
        )
      );

      const itemsWithError = data.filter((item) => item.error);

      itemsWithError.forEach((item) =>
        useToast.error({
          title: `Error en ${item.risk_code}: ${item.error?.message}`,
          id: 'checklight-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Debug removed

  const transformData = (rawData: riskData[] | any): RiskDataMap => {
    if (!Array.isArray(rawData)) return {};
    return rawData.reduce<RiskDataMap>((acc, item) => {
      acc[item.risk_code] = {
        error: item.error,
        risk_code: item.risk_code,
        last_execution: item.last_execution || item.execution_date,
        indicators: item.indicators || [],
      };
      return acc;
    }, {});
  };

  const getLastExecutionFromData = (entries: RiskDataEntry[]) => {
    const lastExecutions = entries
      .map((item) => item.last_execution)
      .filter(Boolean) as string[];

    if (lastExecutions.length === 0) return '-';

    const parsed = lastExecutions.map((text: string) => ({
      text,
      daysAgo: parseLastExecution(text),
    }));

    parsed.sort((a, b) => a.daysAgo - b.daysAgo);

    return parsed[0]?.text || '-';
  };

  const parseLastExecution = (text: string): number => {
    if (text === 'Hoy') {
      return 0;
    }
    const match = text.match(/Hace (\d+) día(s)?/);
    if (match) {
      return parseInt(match[1], 10);
    }
    return 9999;
  };

  const getBadgeStatus = (status: string) => {
    const mapStatus = new Map();
    mapStatus.set(RISK_STATUS.approved.label, 'success');
    mapStatus.set(RISK_STATUS.revision.label, 'warning');
    mapStatus.set(RISK_STATUS.medium_risk.label, 'warning');
    mapStatus.set(RISK_STATUS.high_risk.label, 'danger');
    return mapStatus.get(status) ? mapStatus.get(status) : 'success';
  };

  const getStatusText = (statusCode: string) => {
    const status = Object.values(RISK_STATUS).find(
      (item) => item.label === statusCode
    );
    return status ? status.text : 'Estado desconocido';
  };

  const getDebtStatusFromSituation = (situation?: string) => {
    if (!situation) return null;

    const numericSituation = Number(situation);
    if (Number.isNaN(numericSituation)) return null;

    if (numericSituation === 1)
      return { text: 'Seguro', variant: 'success' as const };
    if (numericSituation === 2 || numericSituation === 3) {
      return { text: 'Revisar', variant: 'warning' as const };
    }
    if (numericSituation > 3)
      return { text: 'Riesgo Alto', variant: 'danger' as const };

    return null;
  };

  const formatPostBalanceLabel = (row: PostBalanceRow) => {
    if (row.label) return row.label;
    if (row.month !== undefined && row.month !== null) {
      const monthIndex = Math.max(0, Number(row.month) - 1);
      const monthName = MONTH_NAMES[monthIndex] || '';
      const yearText = row.year ? ` ${row.year}` : '';
      return `${monthName}${yearText}`.trim();
    }
    return '';
  };

  const formatCurrencyString = (value: number | string) =>
    String(formatCurrency(Number(value || 0), true));

  const formatBpbIndicatorValue = (value: string | undefined): string => {
    if (value == null || value === '') return value ?? '';
    const s = String(value).trim();
    // Convertir las leyendas viejas a N/A
    if (['Sin ventas', 'Sin deudas', 'Sin patrimonio neto'].includes(s)) {
      return 'N/A';
    }
    // Si es N/A o empieza con "Sin " (para casos edge), devolver N/A
    if (/^Sin |^N\/A$/i.test(s)) {
      return 'N/A';
    }
    if (s.endsWith('%')) {
      const num = parseFloat(s) || (s.includes(',') ? toNumber(s) : NaN);
      return Number.isNaN(num) ? s : `${Math.round(num)}%`;
    }
    if (s.includes('$')) {
      const neg = /^-/.test(s);
      const numStr = s.replace(/^-\$?\s*|\$?\s*/g, '').replace(/\s/g, '');
      const num = numStr.includes(',') ? toNumber(numStr) : parseFloat(numStr);
      if (Number.isNaN(num)) return s;
      const formatted = Math.round(num).toLocaleString('es-AR', { maximumFractionDigits: 0 });
      return (neg ? '-$ ' : '$ ') + formatted;
    }
    const num = parseFloat(s) || (s.includes(',') ? toNumber(s) : NaN);
    if (Number.isNaN(num)) return s;
    return Math.round(num).toLocaleString('es-AR', { maximumFractionDigits: 0 });
  };

  const handleCopyDocument = async (document?: string) => {
    if (!document) return;

    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      useToast.error({
        title: 'No se pudo copiar el documento',
        id: 'checklight-toast',
        description: 'Funcionalidad no soportada en este navegador.',
        closeButton: true,
        autoClose: 3000,
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(document);
      useToast.success({
        title: 'Documento copiado',
        id: 'checklight-toast',
        description: document,
        closeButton: true,
        autoClose: 2000,
      });
    } catch (error) {
      useToast.error({
        title: 'No se pudo copiar el documento',
        id: 'checklight-toast',
        description: 'Intentá nuevamente.',
        closeButton: true,
        autoClose: 3000,
      });
    }
  };

  const getPostBalanceColumns = (header: string) => {
    return [
      {
        name: header,
        selector: (row: PostBalanceRow) => (
          <Card>
            <P
              type={
                row.label === 'Total anualizado' || row.label === 'Promedio'
                  ? '3'
                  : '4'
              }
              variant={
                row.label === 'Promedio'
                  ? 'primary'
                  : row.label === 'Total anualizado'
                    ? 'neutral-1'
                    : 'neutral'
              }
              modifier='regular'
              weight={
                row.label === 'Total anualizado'
                  ? 'bold'
                  : row.label === 'Promedio'
                    ? 'bold'
                    : 'regular'
              }
            >
              {formatPostBalanceLabel(row)}
            </P>
          </Card>
        ),
        grow: 2,
      },
      {
        name: '',
        selector: (row: PostBalanceRow) => (
          <Card>
            <Span
              type={
                row.label === 'Total anualizado' || row.label === 'Promedio'
                  ? '3'
                  : '4'
              }
              variant={
                row.label === 'Promedio'
                  ? 'primary'
                  : row.label === 'Total anualizado'
                    ? 'neutral-1'
                    : 'neutral'
              }
              modifier='regular'
              weight={
                row.label === 'Total anualizado'
                  ? 'bold'
                  : row.label === 'Promedio'
                    ? 'bold'
                    : 'regular'
              }
            >
              $ {formatCurrency(row.amount ?? 0, true)}
            </Span>
          </Card>
        ),
        right: true,
      },
    ];
  };

  const getColumns = (
    firstHeader: string = '',
    secondHeader: string = '',
    thirdHeader: string = ''
  ) => {
    return [
      {
        name: firstHeader,
        selector: (row: indicator) => (
          <Card>
            <P type='3' variant='neutral'>
              {row.description}
            </P>
          </Card>
        ),
        grow: 1.3,
      },
      {
        name: secondHeader,
        selector: (row: indicator) => (
          <Card>
            <Tooltip
              position='top'
              msg={row.tooltip_message ? row.tooltip_message : row.value}
            >
              <Span type='3' variant='neutral'>
                {row.value}
              </Span>
            </Tooltip>
          </Card>
        ),
      },
      {
        name: thirdHeader,
        selector: (row: indicator) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {row.status ? (
              <Badge variant={getBadgeStatus(row.status)} type='text' size='sm'>
                {getStatusText(row.status)}
              </Badge>
            ) : (
              ''
            )}
          </div>
        ),
        grow: 1.2,
      },
    ];
  };

  const getShareholdersColumns = () => {
    return [
      {
        name: 'Nombre',
        selector: (row: indicator) => (
          <Card>
            <P type='3' variant='neutral'>
              {row.description}
            </P>
          </Card>
        ),
        grow: displayFlex === 'flex' ? 1.8 : 5,
      },
      {
        name: 'Cuit',
        selector: (row: indicator) => (
          <Card display='flex' alignItems='center' gap='8px'>
            <Span type='3' variant='neutral'>
              {row.document || '-'}
            </Span>
            {row.document ? (
              <Tooltip position='top' msg='Copiar documento'>
                <Icon
                  name='copy_outline'
                  variant='neutral'
                  modifier='regular'
                  width={18}
                  height={18}
                  cursor='pointer'
                  onClick={() => handleCopyDocument(row.document)}
                />
              </Tooltip>
            ) : null}
          </Card>
        ),
        grow: 2,
      },
      {
        name: '%',
        selector: (row: indicator) => {
          // Mostrar el porcentaje de participación si está disponible
          const participation = row.ownership_interest;

          // Convertir a número si es string o validar que sea un número
          const participationNumber =
            typeof participation === 'string'
              ? parseFloat(participation)
              : participation;

          const displayValue =
            participationNumber !== undefined &&
            participationNumber !== null &&
            !isNaN(participationNumber)
              ? `${participationNumber.toFixed(2)}%`
              : '-';

          return (
            <Card>
              <Span type='3' variant='neutral'>
                {displayValue}
              </Span>
            </Card>
          );
        },
        grow: 1,
      },
      {
        name: 'Score Nosis',
        selector: (row: indicator) => (
          <Card>
            <Tooltip position='top' msg={row.value || '-'}>
              <Span type='3' variant='neutral'>
                {row.value}
              </Span>
            </Tooltip>
          </Card>
        ),
        grow: 1.4,
      },
      {
        name: 'Bienes',
        selector: (row: indicator) => {
          const assetsValue = row.assets || '-';
          return (
            <Card>
              <Tooltip position='top' msg={assetsValue}>
                <Span type='3' variant='neutral'>
                  {assetsValue}
                </Span>
              </Tooltip>
            </Card>
          );
        },
        grow: 2,
      },
      {
        name: 'Valor',
        selector: (row: indicator) => {
          // Usar total_value si está disponible, sino amount
          const value = row.total_value ?? row.amount ?? 0;

          // Formatear como moneda argentina
          const formattedValue =
            typeof value === 'number'
              ? new Intl.NumberFormat('es-AR', {
                  style: 'currency',
                  currency: 'ARS',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(value)
              : value || '-';

          return (
            <Card>
              <Tooltip position='top' msg={formattedValue}>
                <Span type='3' variant='neutral'>
                  {formattedValue}
                </Span>
              </Tooltip>
            </Card>
          );
        },
        grow: 1.6,
      },
    ];
  };

  const getBpbData = (): indicator[] => {
    const bpbIndicators = riskDataMap?.[riskEntities.bpb.label]?.indicators;
    if (!bpbIndicators) return [];

    const bpbDataOrdered = [...bpbIndicators].sort(
      (a: indicator, b: indicator) => {
        const indicatorA = parseInt(a.code.match(/BPB_(\d+)$/)?.[1] || '0', 10);
        const indicatorB = parseInt(b.code.match(/BPB_(\d+)$/)?.[1] || '0', 10);
        return indicatorA - indicatorB;
      }
    );

    return bpbDataOrdered;
  };

  const getNosisData = (): indicator[] => {
    const nosisIndicators = riskDataMap?.[riskEntities.nosis.label]?.indicators;
    if (!nosisIndicators) return [];

    const nosisData = [...nosisIndicators].sort(
      (a: indicator, b: indicator) => {
        const indicatorA = parseInt(
          a.code.match(/NOSIS_(\d+)$/)?.[1] || '0',
          10
        );
        const indicatorB = parseInt(
          b.code.match(/NOSIS_(\d+)$/)?.[1] || '0',
          10
        );
        return indicatorA - indicatorB;
      }
    );

    const filtered = nosisData.filter((item: indicator) => {
      const match = item.code.match(/NOSIS_(\d+)$/);
      return match && match[1].length <= 3;
    });

    return filtered;
  };

  const getShareholdersData = (): indicator[] => {
    const nosisIndicators = riskDataMap?.[riskEntities.nosis.label]?.indicators;
    if (!nosisIndicators) return [];

    const shareholderIndicators = nosisIndicators.filter((item: indicator) => {
      const match = item.code.match(/NOSIS_(\d+)$/);
      return match && match[1].length > 3;
    });

    return shareholderIndicators.map((item: indicator) => {
      // Usar directamente los campos del objeto
      // Los datos ahora vienen directamente desde el backend
      return {
        ...item,
        document: item.document || '', // Ya viene del backend
        assets: item.assets || '', // Ya viene del backend
        amount: item.total_value ?? item.amount ?? 0, // Priorizar total_value
      };
    });
  };

  const renderErrorText = () => {
    return (
      <Card margin={{ top: '1' }}>
        <P type='2' variant='warning' withMargin={false}>
          No se pudieron obtener datos, intentá nuevamente.
        </P>
        <P type='2' variant='warning' withMargin={false}>
          Se muestran los últimos datos obtenidos.
        </P>
      </Card>
    );
  };

  const getFallOfTheDebtColumns = () => {
    return [
      {
        name: 'Banco',
        selector: (row: FallOfDebtRow) => (
          <Card>
            <P
              type='3'
              variant='neutral'
              weight={row.isTotal ? 'bold' : 'regular'}
            >
              {row.name}
            </P>
          </Card>
        ),
        grow: 2,
      },
      {
        name: 'A 12 meses',
        selector: (row: FallOfDebtRow) => (
          <Card>
            <Span
              type='3'
              variant='neutral'
              weight={row.isTotal ? 'bold' : 'regular'}
            >
              {row.twelve_months_amount ? row.twelve_months_amount : '-'}
            </Span>
          </Card>
        ),
        grow: 2,
      },
      {
        name: 'A 6 meses',
        selector: (row: FallOfDebtRow) => (
          <Card>
            <Span
              type='3'
              variant='neutral'
              weight={row.isTotal ? 'bold' : 'regular'}
            >
              {row.six_months_amount ? row.six_months_amount : '-'}
            </Span>
          </Card>
        ),
        grow: 2,
      },
      {
        name: 'Actual',
        selector: (row: FallOfDebtRow) => (
          <Card>
            <Tooltip
              position='top'
              msg={row.current_amount ? row.current_amount : '-'}
            >
              <Span
                type='3'
                variant='neutral'
                weight={row.isTotal ? 'bold' : 'regular'}
              >
                {row.current_amount ? row.current_amount : '-'}
              </Span>
            </Tooltip>
          </Card>
        ),
        grow: 1,
      },
      {
        name: 'Situación',
        selector: (row: FallOfDebtRow) => (
          <Card>
            <Span
              type='3'
              variant='neutral'
              weight={row.isTotal ? 'bold' : 'regular'}
            >
              {row.situation ? row.situation : '-'}
            </Span>
          </Card>
        ),
        grow: 1,
      },
      {
        name: 'Días de atraso',
        selector: (row: FallOfDebtRow) => (
          <Card>
            <Span
              type='3'
              variant='neutral'
              weight={row.isTotal ? 'bold' : 'regular'}
            >
              {row.daysLate ? row.daysLate : '-'}
            </Span>
          </Card>
        ),
        grow: 1,
      },
      {
        name: 'Estado',
        selector: (row: FallOfDebtRow) => {
          const statusInfo = getDebtStatusFromSituation(row.situation);
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {statusInfo ? (
                <Badge variant={statusInfo.variant} type='text' size='sm'>
                  {statusInfo.text}
                </Badge>
              ) : (
                <Span
                  type='3'
                  variant='neutral'
                  weight={row.isTotal ? 'bold' : 'regular'}
                >
                  -
                </Span>
              )}
            </div>
          );
        },
        grow: 1,
      },
    ];
  };

  const getFallOfTheDebtTableData = (): FallOfDebtRow[] => {
    const bankDetails =
      debts?.banks?.detail?.map(
        (bank: BankDetail): FallOfDebtRow => ({
          ...bank,
          daysLate: bank.days_past_due,
        })
      ) || [];

    const totalRow: FallOfDebtRow = {
      name: 'Total',
      twelve_months_amount: debts?.banks?.twelve_months_amount_total,
      six_months_amount: debts?.banks?.six_months_amount_total,
      current_amount: debts?.banks?.current_amount_total,
      situation: debts?.banks?.situation_total,
      daysLate: debts?.banks?.days_late_total,
      status: debts?.banks?.status_total,
      isTotal: true,
    };
    return [...bankDetails, totalRow];
  };

  const buildSgrTableData = (): SgrRow[] => {
    const rows =
      debts?.sgrs?.map(
        (sgr: SgrItem): SgrRow => ({
          name: sgr.name,
          daysLate: sgr.days_past_due,
          balance: sgr.current_amount,
        })
      ) || [];

    if (rows.length > 0) {
      rows.push({
        name: 'Total',
        daysLate: '',
        balance: String(
          formatCurrency(
            (debts?.sgrs || []).reduce(
              (acc: number, sgr: SgrItem) => {
                // remover "$", espacios y luego parsear
                const cleanedValue = (sgr.current_amount || '0')
                  .toString()
                  .replace(/\$/g, '')
                  .replace(/\s/g, '')
                  .trim();
                return acc + toNumber(cleanedValue);
              },
              0
            ),
            true
          )
        ),
        isTotal: true,
      });
    }

    return rows;
  };

  const getSgrColumns = () => [
    {
      name: 'SGR',
      selector: (row: SgrRow) => (
        <Tooltip position='top' msg={row.name}>
          <P
            type='3'
            variant='neutral'
            weight={row.isTotal ? 'bold' : 'regular'}
          >
            {row.name}
          </P>
        </Tooltip>
      ),
      grow: 1,
    },
    {
      name: 'Días de Atraso',
      selector: (row: SgrRow) => (
        <Span
          type='3'
          variant='neutral'
          weight={row.isTotal ? 'bold' : 'regular'}
        >
          {row.daysLate}
        </Span>
      ),
      grow: 1,
    },
    {
      name: 'Saldo',
      selector: (row: SgrRow) => (
        <Tooltip position='top' msg={row.balance}>
          <Span
            type='3'
            variant='neutral'
            weight={row.isTotal ? 'bold' : 'regular'}
          >
            {row.balance}
          </Span>
        </Tooltip>
      ),
      grow: 1,
    },
  ];

  const toggleSgrRow = (sgrName: string) => {
    setExpandedSgrRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sgrName)) {
        newSet.delete(sgrName);
      } else {
        newSet.add(sgrName);
      }
      return newSet;
    });
  };

  const calculateInstrumentTotal = (
    instruments: SgrInstrumentDetail[],
    instrumentName: string
  ) => {
    const instrument = instruments.find((i) => i.name === instrumentName);
    if (!instrument) return 0;
    return (
      (instrument.lessThanTwelveAmount || 0) +
      (instrument.betweenTwelveFortyEightAmount || 0) +
      (instrument.moreThanFortyEightAmount || 0)
    );
  };

  const buildSgrByInstrumentTableData = (): SgrInstrumentRow[] => {
    const sgrs = debts?.sgr_by_instrument?.sgrs || [];
    const total = debts?.sgr_by_instrument?.total;

    const rows: SgrInstrumentRow[] = [];

    // Add rows for each SGR
    sgrs.forEach((sgr: SgrByInstrumentEntry) => {
      const isExpanded = isPdfMode || expandedSgrRows.has(sgr.name);

      // Main row with totals
      rows.push({
        type: 'main',
        sgrName: sgr.name,
        bancario: formatCurrencyString(
          calculateInstrumentTotal(sgr.instruments, 'Bancario')
        ),
        chequeTercero: formatCurrencyString(
          calculateInstrumentTotal(sgr.instruments, 'Cheque de Tercero')
        ),
        chequePropio: formatCurrencyString(
          calculateInstrumentTotal(sgr.instruments, 'Cheque Propio')
        ),
        pagare: formatCurrencyString(
          calculateInstrumentTotal(sgr.instruments, 'Pagaré')
        ),
        on: formatCurrencyString(
          calculateInstrumentTotal(sgr.instruments, 'On')
        ),
        otros: formatCurrencyString(
          calculateInstrumentTotal(sgr.instruments, 'Otros')
        ),
        total: formatCurrencyString(
          calculateInstrumentTotal(sgr.instruments, 'Total')
        ),
        isExpanded,
      });

      // Add detail rows if expanded
      if (isExpanded) {
        const periods = [
          { label: '< 12 meses', field: 'lessThanTwelveAmount' },
          { label: 'E. 12 y 48', field: 'betweenTwelveFortyEightAmount' },
          { label: '> 48 meses', field: 'moreThanFortyEightAmount' },
        ] as const;

        periods.forEach((period) => {
          const bancarioInst = sgr.instruments.find(
            (i: SgrInstrumentDetail) => i.name === 'Bancario'
          );
          const chequeTerceroInst = sgr.instruments.find(
            (i: SgrInstrumentDetail) => i.name === 'Cheque de Tercero'
          );
          const chequePropioInst = sgr.instruments.find(
            (i: SgrInstrumentDetail) => i.name === 'Cheque Propio'
          );
          const pagareInst = sgr.instruments.find(
            (i: SgrInstrumentDetail) => i.name === 'Pagaré'
          );
          const onInst = sgr.instruments.find(
            (i: SgrInstrumentDetail) => i.name === 'On'
          );
          const otrosInst = sgr.instruments.find(
            (i: SgrInstrumentDetail) => i.name === 'Otros'
          );

          const bancarioVal = bancarioInst?.[period.field] || 0;
          const chequeTerceroVal = chequeTerceroInst?.[period.field] || 0;
          const chequePropioVal = chequePropioInst?.[period.field] || 0;
          const pagareVal = pagareInst?.[period.field] || 0;
          const onVal = onInst?.[period.field] || 0;
          const otrosVal = otrosInst?.[period.field] || 0;
          const totalVal =
            bancarioVal +
            chequeTerceroVal +
            chequePropioVal +
            pagareVal +
            onVal +
            otrosVal;

          rows.push({
            type: 'detail',
            sgrName: period.label,
            bancario: formatCurrencyString(bancarioVal),
            chequeTercero: formatCurrencyString(chequeTerceroVal),
            chequePropio: formatCurrencyString(chequePropioVal),
            pagare: formatCurrencyString(pagareVal),
            on: formatCurrencyString(onVal),
            otros: formatCurrencyString(otrosVal),
            total: formatCurrencyString(totalVal),
          });
        });
      }
    });

    // Add total row
    if (total) {
      rows.push({
        type: 'total',
        sgrName: 'Total',
        bancario: formatCurrencyString(total.bankingTotalAmount || 0),
        chequeTercero: formatCurrencyString(
          total.checksTerceroTotalAmount || 0
        ),
        chequePropio: formatCurrencyString(total.checksPropioTotalAmount || 0),
        pagare: formatCurrencyString(total.promissoryNotesTotalAmount || 0),
        on: formatCurrencyString(total.onTotalAmount || 0),
        otros: formatCurrencyString(total.othersTotalAmount || 0),
        total: formatCurrencyString(total.totalAmount || 0),
      });
    }

    return rows;
  };

  const getSgrByInstrumentColumns = () => [
    {
      selector: (row: SgrInstrumentRow) => (
        <Card>
          <P
            type='3'
            variant='neutral-1'
            weight={row.type === 'detail' ? 'regular' : 'bold'}
          >
            {row.sgrName}
          </P>
        </Card>
      ),
      grow: 2,
    },
    {
      name: 'Bancario',
      selector: (row: SgrInstrumentRow) => (
        <Card>
          <Span
            type='3'
            variant='neutral-1'
            weight={row.type === 'total' ? 'bold' : 'regular'}
          >
            $ {row.bancario}
          </Span>
        </Card>
      ),
      grow: 1,
    },
    {
      name: 'Cheque Tercero',
      selector: (row: SgrInstrumentRow) => (
        <Card>
          <Span
            type='3'
            variant='neutral-1'
            weight={row.type === 'total' ? 'bold' : 'regular'}
          >
            $ {row.chequeTercero}
          </Span>
        </Card>
      ),
      grow: 1,
    },
    {
      name: 'Cheque Propio',
      selector: (row: SgrInstrumentRow) => (
        <Card>
          <Span
            type='3'
            variant='neutral-1'
            weight={row.type === 'total' ? 'bold' : 'regular'}
          >
            $ {row.chequePropio}
          </Span>
        </Card>
      ),
      grow: 1,
    },
    {
      name: 'Pagaré',
      selector: (row: SgrInstrumentRow) => (
        <Card>
          <Span
            type='3'
            variant='neutral-1'
            weight={row.type === 'total' ? 'bold' : 'regular'}
          >
            $ {row.pagare}
          </Span>
        </Card>
      ),
      grow: 1,
    },
    {
      name: 'On',
      selector: (row: SgrInstrumentRow) => (
        <Card>
          <Span
            type='3'
            variant='neutral-1'
            weight={row.type === 'total' ? 'bold' : 'regular'}
          >
            $ {row.on}
          </Span>
        </Card>
      ),
      grow: 1,
    },
    {
      name: 'Otros',
      selector: (row: SgrInstrumentRow) => (
        <Card>
          <Span
            type='3'
            variant='neutral-1'
            weight={row.type === 'total' ? 'bold' : 'regular'}
          >
            $ {row.otros}
          </Span>
        </Card>
      ),
      grow: 1,
    },
    {
      name: 'Total',
      selector: (row: SgrInstrumentRow) => (
        <Card>
          <Span
            type='3'
            variant={row.type === 'total' ? 'success' : 'neutral-1'}
            weight={row.type === 'total' ? 'bold' : 'regular'}
          >
            $ {row.total}
          </Span>
        </Card>
      ),
      grow: 1,
    },
    {
      name: '',
      selector: (row: SgrInstrumentRow) => (
        <Card>
          {row.type === 'main' && (
            <Icon
              name={
                row.isExpanded ? 'chevron_up_outline' : 'chevron_down_outline'
              }
              variant='neutral'
              modifier='strong'
              width={20}
              height={20}
              cursor='pointer'
              onClick={() => toggleSgrRow(row.sgrName)}
            />
          )}
        </Card>
      ),
      width: '2%',
      right: true,
    },
  ];

  const salesData = [
    ...(postBalanceData?.sales || []),
    {
      label: 'Total anualizado',
      amount: postBalanceData?.salesAnnualized || 0,
    },
    {
      label: 'Promedio',
      amount: postBalanceData?.salesAverage || 0,
    },
  ];

  const purchasesData = [
    ...(postBalanceData?.purchases || []),
    {
      label: 'Total anualizado',
      amount: postBalanceData?.purchasesAnnualized || 0,
    },
    {
      label: 'Promedio',
      amount: postBalanceData?.purchasesAverage || 0,
    },
  ];

  const nosisIndicators = riskDataMap?.[riskEntities.nosis.label]?.indicators
    ?.length
    ? getNosisData()
    : [];
  const nosisRejectedIndicators = nosisIndicators.slice(4);

  const getIndicatorByLabel = (label: string) =>
    nosisIndicators.find(
      (item) =>
        item.description.toLowerCase().indexOf(label.toLowerCase()) !== -1
    );

  const getStatusBadge = (status?: string | null) =>
    status ? (
      <Badge variant={getBadgeStatus(status)} type='text' size='sm'>
        {getStatusText(status)}
      </Badge>
    ) : null;

  return (
    <Card>
      <Toast id='checklight-toast' />
      {isLoading ? (
        <Card
          display='flex'
          justifyContent='center'
          alignItems='center'
          height='200px'
        >
          <Spinner size='lg' variant='primary' />
        </Card>
      ) : riskDataMap ? (
        <Card>
          {showBalanceInfo &&
            riskDataMap?.[riskEntities.bpb.label]?.indicators &&
            (isLoading ? (
              <Spinner size='sm' variant='primary' />
            ) : (
              <Card width='100%'>
                <Card
                  variant={clientTheme ? 'primary' : 'success'}
                  modifier='weak'
                  spacing={'2'}
                  display='flex'
                  justifyContent='space-between'
                  alignItems='center'
                >
                  <H
                    type='3'
                    variant='neutral-1'
                    withMargin={false}
                    weight='bold'
                    modifier='strong'
                  >
                    Balance y ventas posteriores al último balance
                  </H>
                  {!isPdfMode && (
                    <Button
                      variant='primary'
                      size='md'
                      icon='download_outline'
                      iconAlign='left'
                      onClick={onDownloadPdf}
                      disabled={!onDownloadPdf || isPdfLoading}
                    >
                      {isPdfLoading ? (
                        <Spinner size='sm' variant='primary' />
                      ) : (
                        'Descargar Análisis PDF'
                      )}
                    </Button>
                  )}
                </Card>
                <Card
                  display='grid'
                  width='100%'
                  margin={{ top: '3' }}
                  style={{
                    gridTemplateColumns:
                      windowWidth >= 900
                        ? 'repeat(3, minmax(0, 1fr))'
                        : windowWidth >= 600
                          ? 'repeat(2, minmax(0, 1fr))'
                          : 'minmax(0, 1fr)',
                    gap: '24px',
                    alignItems: 'flex-start',
                  }}
                >
                  {getBpbData().map((indicator) => (
                    <Card
                      key={indicator.code}
                      display='flex'
                      flexDirection='column'
                      gap='8px'
                      style={{ minWidth: 0 }}
                    >
                      <P
                        type='4'
                        variant='neutral-1'
                        weight='bold'
                        withMargin={false}
                      >
                        {indicator.description}
                      </P>
                      <Card
                        display='flex'
                        alignItems='center'
                        flexWrap='wrap'
                        gap='8px'
                      >
                        <P
                          type='3'
                          variant='neutral-1'
                          weight='bold'
                          withMargin={false}
                        >
                          {formatBpbIndicatorValue(indicator.value)}
                        </P>
                        {indicator.status && (
                          <Badge
                            variant={getBadgeStatus(indicator.status)}
                            type='text'
                            size='sm'
                          >
                            {getStatusText(indicator.status)}
                          </Badge>
                        )}
                      </Card>
                    </Card>
                  ))}
                </Card>
              </Card>
            ))}
          {balanceData && (
            <Card margin={{ top: '6' }}>
              <Card
                style={{
                  paddingBottom: '6px',
                  marginBottom: '8px',
                }}
              >
                <Collapsable
                  title='Ver balance y ventas posteriores al último balance'
                  titleVariant='primary'
                  titleModifier='strong'
                  iconVariant='primary'
                  startOpened={isPdfMode ? true : false}
                  forceOpen={isPdfMode}
                  hideToggle={isPdfMode}
                  headerStyle={(open) =>
                    open
                      ? { borderBottom: '1px solid #03BAA1', borderRadius: 0, paddingBottom: '6px' }
                      : { border: '1px solid #03BAA1', borderRadius: '6px', padding: '6px 16px' }
                  }
                  children={
                    <Card margin={{ top: '3' }}>
                      {balanceData ? (
                        <Visualizer
                          riskData={
                            isStandardizedBalanceData(balanceData)
                              ? balanceData
                              : standardizeBalanceData(balanceData)
                          }
                          withCollapsables={true}
                          isPdfMode={isPdfMode}
                        />
                      ) : (
                        <Card>
                          <P type='1' variant='warning' align='center'>
                            No hay datos de balance disponibles.
                          </P>
                        </Card>
                      )}
                      <Card display={displayFlex} gap='16px'>
                        <Card width={displayFlex === 'flex' ? '52%' : '100%'}>
                          <Card width='100%'>
                            <Table
                              columns={getPostBalanceColumns('Ventas')}
                              tableData={{
                                size: salesData.length,
                                totalElements: salesData.length,
                              }}
                              data={salesData}
                              variant='weak'
                              conditionalRowStyles={[
                                {
                                  when: (row: PostBalanceRow) =>
                                    row.label === 'Total anualizado',
                                  style: { borderTop: '2px solid #4A4A4A' },
                                },
                              ]}
                            />
                          </Card>
                        </Card>
                        <Card
                          width={displayFlex === 'flex' ? '52%' : '100%'}
                          margin={
                            displayFlex === 'flex' ? { top: '0' } : { top: '5' }
                          }
                        >
                          <Card
                            width={displayFlex === 'flex' ? '100%' : '100%'}
                          >
                            <Table
                              columns={getPostBalanceColumns('Compras')}
                              tableData={{
                                size: purchasesData.length,
                                totalElements: purchasesData.length,
                              }}
                              data={purchasesData}
                              variant='weak'
                              conditionalRowStyles={[
                                {
                                  when: (row: PostBalanceRow) =>
                                    row.label === 'Total anualizado',
                                  style: { borderTop: '2px solid #4A4A4A' },
                                },
                              ]}
                            />
                          </Card>
                        </Card>
                      </Card>
                      {!isPdfMode && hideInfo && (
                        <Card margin={{ top: '5', bottom: '5' }}>
                          <Card
                            display='flex'
                            justifyContent='space-between'
                            gap='16px'
                          >
                            <FilePreview
                              requiredDocumentId='010'
                              file={documents?.find(
                                (doc) => doc.type === '010'
                              )}
                              cuit={cuit || ''}
                            />
                            <FilePreview
                              requiredDocumentId='009'
                              file={documents?.find(
                                (doc) => doc.type === '009'
                              )}
                              cuit={cuit || ''}
                            />
                          </Card>
                        </Card>
                      )}
                    </Card>
                  }
                />
              </Card>
            </Card>
          )}
          {riskDataMap?.[riskEntities.nosis.label]?.indicators?.length > 0 && (
            <Card margin={{ top: '4' }} data-pdf-page-break="before">
              <Card
                variant={clientTheme ? 'primary' : 'success'}
                modifier='weak'
                spacing={'2'}
              >
                <H
                  type='3'
                  variant='neutral-1'
                  withMargin={false}
                  weight='bold'
                  modifier='strong'
                >
                  Nosis
                </H>
              </Card>
              <Card
                margin={{ top: '4' }}
                display='flex'
                justifyContent='space-between'
                gap='24px'
                alignItems='center'
              >
                <P
                  type='1'
                  variant='neutral-1'
                  withMargin={false}
                  weight='bold'
                  modifier='strong'
                >
                  Chequeo de riesgo
                </P>

                <Card display='flex' gap='24px' alignItems='center'>
                  <P
                    type='2'
                    variant='neutral'
                    modifier='weak'
                    withMargin={false}
                  >
                    Última consulta: {lastExecution}
                  </P>
                  {!hideInfo && (
                    <Card width='210px'>
                      <Button
                        id='client-button-save'
                        size='lg'
                        variant='secondary'
                        fullWidth
                        onClick={() => handleRiskDataAndDebts()}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <Spinner size='sm' variant='primary' />
                        ) : (
                          'Realizar consulta'
                        )}
                      </Button>
                    </Card>
                  )}
                </Card>
              </Card>

              {riskDataMap?.[riskEntities.nosis.label]?.error !== null &&
                renderErrorText()}
              <Card margin={{ bottom: '5' }}>
                <Card
                  display='grid'
                  gap='20px'
                  margin={{ top: '3', bottom: '3' }}
                  style={{
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    alignItems: 'flex-start',
                    columnGap: '24px',
                  }}
                >
                  {[
                    {
                      title: 'Score Nosis actual',
                      value:
                        getIndicatorByLabel('score nosis actual')?.value || '-',
                      status: getIndicatorByLabel('score nosis actual')?.status,
                    },
                    {
                      title: 'Score Nosis 365 dias',
                      value:
                        getIndicatorByLabel('score nosis 365')?.value || '-',
                      status: getIndicatorByLabel('score nosis 365')?.status,
                    },
                    {
                      title: 'Cheques rechazados (u. 12 meses)',
                      value:
                        getIndicatorByLabel('cheques rechazados')?.value ||
                        'NO',
                      status: getIndicatorByLabel('cheques rechazados')?.status,
                    },
                    {
                      title: 'Otros antecedente neg.',
                      value: getIndicatorByLabel('antecedente')?.value || 'NO',
                      status: getIndicatorByLabel('antecedente')?.status,
                    },
                  ].map((item) => (
                    <Card
                      key={item.title}
                      display='flex'
                      flexDirection='column'
                      gap='6px'
                    >
                      <P
                        type='4'
                        variant='neutral-1'
                        withMargin={false}
                        weight='bold'
                      >
                        {item.title}
                      </P>
                      <Card
                        display='flex'
                        alignItems='flex-start'
                        flexWrap='wrap'
                        gap='6px'
                        style={{ rowGap: '2px' }}
                      >
                        <P
                          type='3'
                          variant='neutral-1'
                          weight='bold'
                          withMargin={false}
                        >
                          {item.value}
                        </P>
                        {getStatusBadge(item.status)}
                      </Card>
                    </Card>
                  ))}
                </Card>

                <Divider
                  variant='neutral'
                  modifier='regular'
                  height='4px'
                  margin={{ top: '2', bottom: '2' }}
                />

                <Card style={{ maxWidth: '600px' }}>
                  <Table
                    columns={[
                      {
                        name: 'Cheques rechazados (u. 12 meses)',
                        selector: (row: indicator) => (
                          <Card>
                            <P type='3' variant='neutral'>
                              {row.description}
                            </P>
                          </Card>
                        ),
                        grow: 2,
                      },
                      {
                        name: '',
                        selector: (row: indicator) => (
                          <Card>
                            <Span type='3' variant='neutral'>
                              {row.value}
                            </Span>
                          </Card>
                        ),
                        right: true,
                      },
                    ]}
                    tableData={{ size: 10, totalElements: 10 }}
                    data={nosisRejectedIndicators}
                    variant='weak'
                  />
                </Card>

                <Table
                  columns={getShareholdersColumns()}
                  tableData={{ size: 10, totalElements: 10 }}
                  data={getShareholdersData()}
                  variant='weak'
                />
              </Card>
            </Card>
          )}
          {hideInfo && (
            <Card margin={{ top: '4' }}>
              <Card display='flex' justifyContent='space-between' gap='16px'>
                <FilePreview
                  requiredDocumentId='008'
                  file={documents?.find((doc) => doc.type === '008')}
                  cuit={cuit || ''}
                />
                <FilePreview
                  requiredDocumentId='007'
                  file={documents?.find((doc) => doc.type === '007')}
                  cuit={cuit || ''}
                />
              </Card>
            </Card>
          )}
          <Card margin={{ top: '3', bottom: '4' }}>
            <Card
              variant={clientTheme ? 'primary' : 'success'}
              modifier='weak'
              spacing={'2'}
            >
              <H
                type='3'
                variant='neutral-1'
                withMargin={false}
                weight='bold'
                modifier='strong'
              >
                Centrales de deuda
              </H>
            </Card>

            <Card display={displayFlex} margin={{ top: '5' }} gap='16px'>
              {riskDataMap?.[riskEntities.bcra.label]?.indicators?.length >
                0 && (
                <Card
                  style={{ overflowX: 'hidden' }}
                  width={displayFlex === 'flex' ? '50%' : '100%'}
                >
                  <P
                    type='2'
                    variant='neutral-1'
                    withMargin={false}
                    weight='bold'
                    modifier='strong'
                  >
                    Deuda BCRA
                  </P>
                  {riskDataMap?.[riskEntities.bcra.label]?.error !== null &&
                    renderErrorText()}
                  <Table
                    columns={getColumns('Indicador', 'Valor', 'Estado')}
                    tableData={{ size: 10, totalElements: 10 }}
                    data={
                      riskDataMap?.[riskEntities.bcra.label]?.indicators || []
                    }
                    variant='weak'
                  />
                </Card>
              )}
              {isCasfogEnabled &&
                riskDataMap?.[riskEntities.casfog.label]?.indicators?.length >
                  0 && (
                  <Card
                    width={displayFlex === 'flex' ? '50%' : '100%'}
                    margin={
                      displayFlex === 'flex' ? { top: '0' } : { top: '5' }
                    }
                    style={{ overflowX: 'hidden' }}
                  >
                    <P
                      type='2'
                      variant='neutral-1'
                      withMargin={false}
                      weight='bold'
                      modifier='strong'
                    >
                      Deuda con otras S.G.R.
                    </P>
                    {riskDataMap?.[riskEntities.casfog.label]?.error !== null &&
                      renderErrorText()}
                    <Table
                      columns={getColumns('Indicador', 'Valor', 'Estado')}
                      tableData={{ size: 10, totalElements: 10 }}
                      data={
                        riskDataMap?.[riskEntities.casfog.label]?.indicators ||
                        []
                      }
                      variant='weak'
                    />
                  </Card>
                )}
            </Card>
          </Card>
        </Card>
      ) : (
        <Card>
          <Card display='grid' gap='8px' margin={{ bottom: '4', top: '4' }}>
            <P
              type='1'
              variant='neutral-1'
              withMargin={false}
              weight='bold'
              modifier='strong'
            >
              Chequeo de riesgo
            </P>
          </Card>
          <Card
            display='flex'
            justifyContent='center'
            gap='16px'
            margin={{ bottom: '4', top: '7' }}
          >
            <Image name='dashboard_empty' width={300} />
            <Card display='grid' gap='16px' width='500px'>
              <P
                type='1'
                withMargin={false}
                variant='neutral'
                modifier='regular'
                weight='bold'
              >
                No pudimos recuperar la información.
              </P>
              <P
                type='1'
                withMargin={false}
                variant='neutral'
                modifier='regular'
              >
                La consulta falló porque no tenés conexión o bien el servicio no
                está disponible. Volvé a intentarlo en unos minutos.
              </P>
            </Card>
          </Card>
          <Card width='200px' margin='auto'>
            <Button
              size='lg'
              onClick={() => handleRiskData(riskCodes)}
              fullWidth
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner size='sm' variant='primary' />
              ) : (
                'Realizar Consulta'
              )}
            </Button>
          </Card>
        </Card>
      )}
      {debts === undefined ? (
        ''
      ) : (
        <Card>
          {showBalanceInfo && (
            <Card margin={{ top: '4' }}>
              <Card
                style={{
                  paddingBottom: '6px',
                  marginBottom: '8px',
                }}
              >
                <Collapsable
                  title='Ver detalle de deuda'
                  titleVariant='primary'
                  titleModifier='strong'
                  iconVariant='primary'
                  startOpened={true}
                  headerStyle={(open) =>
                    open
                      ? { borderBottom: '1px solid #03BAA1', borderRadius: 0, paddingBottom: '6px' }
                      : { border: '1px solid #03BAA1', borderRadius: '6px', padding: '6px 16px' }
                  }
                  forceOpen={isPdfMode}
                  hideToggle={isPdfMode}
                  children={
                    <Card margin={{ top: '4' }}>
                      <Card>
                        <P
                          type='2'
                          variant='neutral-1'
                          withMargin={false}
                          weight='bold'
                          modifier='strong'
                        >
                          Caída de la deuda
                        </P>

                        <Table
                          data={getFallOfTheDebtTableData()}
                          columns={getFallOfTheDebtColumns()}
                          tableData={{ size: 30, totalElements: 30 }}
                          variant='weak'
                        />
                      </Card>
                      {isCasfogEnabled && (
                        <Card>
                          <Card
                            width={displayFlex === 'flex' ? '52%' : '100%'}
                            margin={
                              displayFlex === 'flex'
                                ? { top: '3' }
                                : { top: '5' }
                            }
                          >
                            <P
                              type='2'
                              variant='neutral-1'
                              withMargin={false}
                              weight='bold'
                              modifier='strong'
                            >
                              Deuda con otras S.G.R.
                            </P>
                            <Card
                              width={displayFlex === 'flex' ? '100%' : '100%'}
                            >
                              <Table
                                data={buildSgrTableData()}
                                columns={getSgrColumns()}
                                tableData={{ size: 30, totalElements: 30 }}
                                variant='weak'
                              />
                            </Card>
                          </Card>
                          <Card margin={{ top: '4' }}>
                            <P
                              type='2'
                              variant='neutral-1'
                              withMargin={false}
                              weight='bold'
                              modifier='strong'
                            >
                              Caída de la deuda con SGR
                            </P>
                            {(() => {
                              const sgrByInstrumentData = buildSgrByInstrumentTableData();
                              return (
                                <Table
                                  data={sgrByInstrumentData}
                                  columns={getSgrByInstrumentColumns()}
                                  tableData={{
                                    size: isPdfMode ? sgrByInstrumentData.length : 30,
                                    totalElements: isPdfMode ? sgrByInstrumentData.length : 30,
                                  }}
                                  variant='weak'
                                  disablePagination={isPdfMode}
                                />
                              );
                            })()}
                          </Card>
                        </Card>
                      )}
                      {hideInfo && (
                        <Card margin={{ top: '4' }}>
                          <Card
                            display='flex'
                            justifyContent='space-between'
                            gap='16px'
                          >
                            <FilePreview
                              requiredDocumentId='003'
                              file={documents?.find(
                                (doc) => doc.type === '003'
                              )}
                              cuit={cuit || ''}
                            />
                            <FilePreview
                              requiredDocumentId='005'
                              file={documents?.find(
                                (doc) => doc.type === '005'
                              )}
                              cuit={cuit || ''}
                            />
                          </Card>
                        </Card>
                      )}
                    </Card>
                  }
                />
              </Card>
            </Card>
          )}
        </Card>
      )}
    </Card>
  );
};

export default CheckLightResume;
